import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUp, Square, Paperclip, Mic, Sparkles, ChevronDown, X, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CAT_FALLBACK = [
  { id: "fast", label: "BDVG Fast" },
  { id: "smart", label: "BDVG Smart" },
  { id: "reasoning", label: "BDVG Reasoning" },
  { id: "creative", label: "BDVG Creative" },
];

export default function Composer({ onSend, streaming, onStop, category, setCategory, categories, autoFocus }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const cats = categories && categories.length ? categories : CAT_FALLBACK;

  useEffect(() => {
    if (autoFocus && taRef.current) taRef.current.focus();
  }, [autoFocus]);

  const grow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 220) + "px";
  };

  const submit = () => {
    const value = text.trim();
    if (!value || streaming) return;
    onSend(value, { attachments });
    setText("");
    setAttachments([]);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
        toast.success(`${f.name} attached — BDVG AI is reading it`);
      } catch (e) {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.onresult = (ev) => {
      const t = Array.from(ev.results).map((r) => r[0].transcript).join(" ");
      setText(t);
      setTimeout(grow, 0);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  const activeCat = cats.find((c) => c.id === category) || cats[0];

  return (
    <div
      className="mx-auto w-full max-w-3xl px-3 pb-4 sm:px-0"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
      }}
    >
      <AnimatePresence>
        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-2 flex flex-wrap gap-2"
          >
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-gold/25 bg-graphite px-3 py-1.5 text-xs" data-testid="composer-attachment">
                <FileText className="h-3.5 w-3.5 text-gold" />
                <span className="max-w-[160px] truncate text-white/80">{a.filename}</span>
                <button onClick={() => setAttachments((x) => x.filter((i) => i.id !== a.id))} data-testid="remove-attachment">
                  <X className="h-3.5 w-3.5 text-white/40 hover:text-white" />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass rounded-2xl border border-white/10 p-2.5 shadow-[0_10px_40px_rgba(0,0,0,0.6)] transition-colors focus-within:border-gold/40">
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            grow();
          }}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Tell BDVG AI what you'd like to do…"
          data-testid="composer-input"
          className="max-h-[220px] w-full resize-none bg-transparent px-3 py-2 text-[15px] text-white placeholder:text-white/35 focus:outline-none"
        />

        <div className="mt-1 flex items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-1">
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="composer-file-input" />
            <IconBtn label="Attach" onClick={() => fileRef.current?.click()} testid="composer-attach">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-[18px] w-[18px]" />}
            </IconBtn>
            <IconBtn label="Voice" onClick={toggleVoice} active={listening} testid="composer-voice">
              <Mic className="h-[18px] w-[18px]" />
            </IconBtn>

            <div className="relative">
              <button
                onClick={() => setModelOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70 transition-colors hover:border-gold/30 hover:text-white"
                data-testid="model-selector-trigger"
              >
                <Sparkles className="h-3.5 w-3.5 text-gold" />
                {activeCat?.label}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <AnimatePresence>
                {modelOpen && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setModelOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      className="absolute bottom-full left-0 z-30 mb-2 w-56 overflow-hidden rounded-xl border border-white/10 bg-black/95 p-1.5 backdrop-blur-xl"
                      data-testid="model-selector-menu"
                    >
                      {cats.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setCategory(c.id);
                            setModelOpen(false);
                          }}
                          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-white/5 ${
                            category === c.id ? "text-gold" : "text-white/75"
                          }`}
                          data-testid={`model-option-${c.id}`}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          {c.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {streaming ? (
            <button
              onClick={onStop}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              data-testid="composer-stop"
            >
              <Square className="h-4 w-4 fill-current" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!text.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-gold-light to-gold text-black shadow-gold-sm transition-all hover:shadow-gold disabled:opacity-30 disabled:shadow-none"
              data-testid="composer-send"
            >
              <ArrowUp className="h-5 w-5" strokeWidth={2.4} />
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-center text-[11px] text-white/25">
        BDVG AI can make mistakes. Verify important information.
      </p>
    </div>
  );
}

function IconBtn({ children, onClick, label, active, testid }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testid}
      className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
        active ? "bg-gold/20 text-gold" : "text-white/50 hover:bg-white/5 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
